// AgentVerse/client/ui/TagInput.js
import { ComponentBase } from "../core/ComponentBase.js";

export class TagInput extends ComponentBase {
    static styles = `
        .tag-input-main-wrapper {
            position: relative; /* For autocomplete positioning */
        }
        .tag-input-container {
            border: 1px solid var(--bs-border-color);
            border-radius: var(--bs-border-radius);
            padding: 0.375rem 0.75rem;
            display: flex;
            flex-wrap: wrap;
            gap: 0.35rem; /* Use gap for consistent spacing */
            align-items: center;
            transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
            /* FIX: Use theme-aware background color */
            background-color: var(--bs-body-bg);
        }
        /* FIX: Add focus state similar to Bootstrap inputs */
        .tag-input-container:focus-within {
            border-color: var(--bs-primary);
            box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
        }
        .tag-item {
            background-color: var(--bs-primary);
            color: var(--bs-primary-contrast, #fff);
            padding: 0.2em 0.6em 0.3em;
            border-radius: var(--ab-radius-xs);
            font-size: 0.875em;
            display: inline-flex;
            align-items: center;
            /* FIX: Remove margins, rely on container's gap */
        }
        .tag-item-remove {
            margin-left: 0.5em;
            cursor: pointer;
            font-weight: bold;
        }
        .tag-input-field {
            border: none;
            outline: none;
            flex-grow: 1;
            padding: 0.25rem 0;
            min-width: 120px;
            /* FIX: Ensure input has transparent background and correct text color */
            background: transparent;
            color: var(--bs-body-color);
        }
        .tag-autocomplete-list {
            list-style: none;
            padding: 0;
            margin: 0;
            position: absolute;
            background-color: var(--bs-body-bg);
            border: 1px solid var(--bs-border-color);
            border-top: none;
            z-index: var(--ab-z-backdrop); /* Higher than Bootstrap's modal backdrop */
            max-height: 200px;
            overflow-y: auto;
            width: 100%;
        }
        .tag-autocomplete-item {
            padding: 0.5rem 0.75rem;
            cursor: pointer;
        }
        .tag-autocomplete-item:hover, .tag-autocomplete-item.active {
            background-color: var(--bs-primary-bg-subtle);
            color: var(--bs-emphasis-color);
        }
        /* Single-select mode */
        .tag-input-single .tag-input-field {
            display: none; /* hidden once a value is selected */
        }
        .tag-input-single.tag-input-empty .tag-input-field {
            display: block; /* visible when nothing selected */
        }
        .tag-input-single-clear {
            margin-left: auto;
            cursor: pointer;
            color: var(--bs-secondary-color);
            font-size: var(--ab-text-sm);
            padding: 0 0.25rem;
        }
        .tag-input-single-clear:hover { color: var(--bs-danger); }
    `;

    constructor(config) {
        super(config);
        this.selectedTags = new Set(); // Store selected tool names
        this.availableOptions = []; // All possible tools { value, label }
        this.filteredOptions = [];
        this.inputValue = "";

        // DOM elements
        this.tagsContainer = null;
        this.inputField = null;
        this.autocompleteList = null;

        this.applyStyles();

        this.creatable = this.config.creatable === true;
        // mode: 'multi' (default, existing behaviour) | 'single' (select one value, returns string)
        this.mode = this.config.mode === 'single' ? 'single' : 'multi';
    }

    async initialize() {
        if (this.config.optionsSourceRpc) {
            await this.loadOptions();
        } else if (this.config.options) {
            this.availableOptions = this.config.options;
        }
        await super.initialize();

    }

    async initAfterLogin() {
        if (this.config.optionsSourceRpc) {
            await this.loadOptions();
        } else if (this.config.options) {
            this.availableOptions = this.config.options;
        }
    }

    async loadOptions() {
        if (!this.config.optionsSourceRpc) {
            this.log.debug("TagInput: No optionsSourceRpc configured.");
            return;
        }

        // --- THIS IS THE FIX: Handle both simple string and complex object for optionsSourceRpc ---
        let rpcMethod;
        let rpcParams = {};

        if (typeof this.config.optionsSourceRpc === 'string') {
            // Handle the simple case: 'RpcClass.methodName'
            rpcMethod = this.config.optionsSourceRpc;
        } else if (typeof this.config.optionsSourceRpc === 'object' && this.config.optionsSourceRpc.method) {
            // Handle the complex case: { method: '...', params: {...} }
            rpcMethod = this.config.optionsSourceRpc.method;
            rpcParams = this.config.optionsSourceRpc.params || {};
        } else {
            this.log.error("Invalid optionsSourceRpc configuration. Must be a string or an object with a 'method' property.", this.config.optionsSourceRpc);
            this.availableOptions = [];
            return;
        }

        try {
            const result = await this.appClient.rpcClient.call(rpcMethod, rpcParams);
            // Defensively handle both plain arrays and { data, totalCount } paginated responses.
            // RPCs that return paginated results should use a dedicated *Options method instead,
            // but this guard prevents a crash if a wrong RPC is accidentally wired up.
            if (Array.isArray(result)) {
                this.availableOptions = result;
            } else if (result && Array.isArray(result.data)) {
                this.log.warn(`TagInput: optionsSourceRpc '${rpcMethod}' returned paginated { data, totalCount } instead of a flat array. Consider using a dedicated options method.`);
                // Coerce raw DB objects into { value, label } if they aren't already.
                this.availableOptions = result.data.map(item => {
                    if (item.value !== undefined && item.label !== undefined) return item;
                    // Common patterns: { _id, name } or { _id, displayName } or { _id, title }
                    const label = item.label || item.name || item.displayName || item.title || item._id?.toString() || String(item);
                    const value = item.value || item._id?.toString() || label;
                    return { value, label };
                });
            } else {
                this.availableOptions = [];
            }
        } catch (error) {
            // If the error is an authentication error and the user is not logged in,
            // gracefully handle it by setting options to empty and not logging a scary error.
            if (error.name === 'Login' && !this.appClient.isLoggedIn()) {
                this.log.debug(`RPC call for TagInput options ('${rpcMethod}') blocked for logged-out user. Setting options to empty.`);
                this.availableOptions = [];
                return; // Exit gracefully
            }

            // For all other errors, log them as a more significant issue.
            this.log.error(`Failed to load options for TagInput via RPC method '${rpcMethod}':`, error);
            this.availableOptions = []; // Ensure it's an empty array on error
        }

    }



    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'tag-input-main-wrapper position-relative'; // For autocomplete positioning
            if (this.mode === 'single') this.container.classList.add('tag-input-single', 'tag-input-empty');

            this.tagsContainer = document.createElement('div');
            this.tagsContainer.className = 'tag-input-container';
            this.container.appendChild(this.tagsContainer);

            this.inputField = document.createElement('input');
            this.inputField.type = 'text';
            this.inputField.className = 'tag-input-field';
            this.inputField.placeholder = this.config.placeholder || 'Add a item..';
            this.tagsContainer.appendChild(this.inputField); // Input inside the bordered container

            this.autocompleteList = document.createElement('ul');
            this.autocompleteList.className = 'tag-autocomplete-list list-group shadow-sm'; // Added Bootstrap classes
            this.autocompleteList.style.display = 'none'; // Initially hidden
            this.container.appendChild(this.autocompleteList); // Autocomplete below the main wrapper

            this.attachEventListeners();
        }
        this.renderTags();
        return this.container;
    }

    attachEventListeners() {
        this.inputField.addEventListener('input', (e) => {
            this.inputValue = e.target.value;
            this.filterOptions();
            this.renderAutocomplete();
        });

        this.inputField.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && this.inputValue.trim() !== "") {
                e.preventDefault();
                const activeAutocompleteItem = this.autocompleteList.querySelector('.active');
                if (activeAutocompleteItem) {
                    this.addTag(activeAutocompleteItem.dataset.value);
                } else if (this.creatable) {
                    // If creatable, add the raw input value as a new tag
                    this.addTag(this.inputValue.trim());
                } else {
                    // If not creatable, only add if it's a valid option
                    const matchedOption = this.availableOptions.find(opt => opt.label.toLowerCase() === this.inputValue.trim().toLowerCase());
                    if (matchedOption) {
                        this.addTag(matchedOption.value);
                    }
                }
                this.clearInputAndAutocomplete();
            } else if (e.key === 'Backspace' && this.inputValue === "" && this.selectedTags.size > 0) {
                const lastTag = Array.from(this.selectedTags).pop();
                this.removeTag(lastTag);
            }
        });


        // Hide autocomplete when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.container.contains(e.target)) {
                this.autocompleteList.style.display = 'none';
            }
        });

        this.inputField.addEventListener('focus', () => {
            this.filterOptions();
            this.renderAutocomplete();
        });
    }


    renderTags() {
        // Clear existing tags except the input field
        while (this.tagsContainer.firstChild && this.tagsContainer.firstChild !== this.inputField) {
            this.tagsContainer.removeChild(this.tagsContainer.firstChild);
        }

        this.selectedTags.forEach(tagValue => {
            const tagElement = document.createElement('span');
            tagElement.className = 'tag-item badge'; // Use Bootstrap badge for styling
            const option = this.availableOptions.find(opt => opt.value === tagValue);
            tagElement.textContent = option ? option.label : tagValue;

            const removeButton = document.createElement('span');
            removeButton.className = 'tag-item-remove ms-1'; // Added ms-1 for spacing
            removeButton.innerHTML = '×'; // 'x' character
            removeButton.addEventListener('click', () => this.removeTag(tagValue));

            tagElement.appendChild(removeButton);
            this.tagsContainer.insertBefore(tagElement, this.inputField);
        });
    }

    filterOptions() {
        if (!this.inputValue) {
            this.filteredOptions = this.availableOptions.filter(opt => !this.selectedTags.has(opt.value)).slice(0, 10); // Show some initial options
        } else {
            this.filteredOptions = this.availableOptions.filter(opt =>
                !this.selectedTags.has(opt.value) &&
                opt.label.toLowerCase().includes(this.inputValue.toLowerCase())
            );
        }
    }

    renderAutocomplete() {
        this.autocompleteList.innerHTML = '';
        if (this.filteredOptions.length > 0) {
            this.filteredOptions.forEach(option => {
                const item = document.createElement('li');
                item.className = 'tag-autocomplete-item list-group-item'; // Bootstrap class
                item.textContent = option.label;
                item.dataset.value = option.value;
                item.addEventListener('click', () => {
                    this.addTag(option.value);
                    this.clearInputAndAutocomplete();
                });
                this.autocompleteList.appendChild(item);
            });
            this.autocompleteList.style.display = 'block';
        } else {
            this.autocompleteList.style.display = 'none';
        }
    }

    addTag(value) {
        if (!value) return;
        if (this.mode === 'single') {
            // Replace any existing selection
            this.selectedTags.clear();
            this.selectedTags.add(value);
            this.container.classList.remove('tag-input-empty');
            this.container.classList.add('tag-input-single');
            // Hide the text input — user picks from dropdown, not free-type
            if (this.inputField) {
                this.inputField.value = '';
                this.inputField.style.display = 'none';
            }
        } else if (!this.selectedTags.has(value)) {
            this.selectedTags.add(value);
        }
        this.renderTags();
        this.emit('change', this.getValue());
    }

    removeTag(value) {
        this.selectedTags.delete(value);
        if (this.mode === 'single') {
            this.container.classList.add('tag-input-empty');
            if (this.inputField) this.inputField.style.display = '';
        }
        this.renderTags();
        this.emit('change', this.getValue());
    }

    clearInputAndAutocomplete() {
        this.inputValue = "";
        this.inputField.value = "";
        this.filteredOptions = [];
        this.autocompleteList.innerHTML = '';
        this.autocompleteList.style.display = 'none';
    }

    getValue() {
        if (this.mode === 'single') {
            return this.selectedTags.size > 0
                ? Array.from(this.selectedTags)[0]
                : null;
        }
        return Array.from(this.selectedTags);
    }

    setValue(values) {
        this.selectedTags.clear();
        if (this.mode === 'single') {
            const v = Array.isArray(values) ? values[0] : values;
            if (v !== undefined && v !== null && v !== '') {
                this.selectedTags.add(v);
                if (this.container) {
                    this.container.classList.remove('tag-input-empty');
                    if (this.inputField) this.inputField.style.display = 'none';
                }
            } else if (this.container) {
                this.container.classList.add('tag-input-empty');
                if (this.inputField) this.inputField.style.display = '';
            }
        } else if (Array.isArray(values)) {
            values.forEach(value => this.selectedTags.add(value));
        }
        this.renderTags();
    }

    destroy() {
        // Remove event listeners, clear DOM
        super.destroy();
    }
}